import { bootstrap as baseBootstrap, } from "@lookiero/messaging";
import { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React, { createContext, useEffect, useMemo, useRef, } from "react";
import invariant from "tiny-invariant";
const contexts = {};
const getContext = ({ id = "" }) => {
    invariant(contexts[id], `There is no bootstrapped messaging context for: ${id}`);
    return contexts[id];
};
const bootstrap = ({ id = "" } = {}) => {
    const { query, command, processManager: baseProcessManager, build, } = baseBootstrap();
    invariant(!contexts[id], `There is already bootstrapped a messaging context for: ${id}`);
    contexts[id] = {
        messaging: createContext({}),
        queryClient: createContext(undefined),
    };
    const instance = {
        query: (name, queryHandler, ...dependencies) => {
            query(name, queryHandler, ...dependencies);
            return instance;
        },
        command: (name, commandHandler, ...dependencies) => (get, save, ...repositoryDependencies) => {
            command(name, commandHandler, ...dependencies)(get, save, ...repositoryDependencies);
            return instance;
        },
        processManager: (name, processManager, ...dependencies) => {
            baseProcessManager(name, processManager, ...dependencies);
            return instance;
        },
        build: () => {
            const { queryBus, commandBus, subscribe, unsubscribe } = build();
            const queryClient = new QueryClient();
            let mountedInstances = 0;
            const Component = ({ children, includeReactQueryDevTools, cacheClearOnUnmount = true, }) => {
                const { messaging: MessagingContext, queryClient: queryClientContext } = getContext({ id });
                const value = useMemo(() => ({
                    queryBus,
                    commandBus,
                    subscribe,
                    unsubscribe,
                }), []);
                // Ref instead of effect dependency: the mount/unmount effect must run
                // exactly once per instance, yet the cleanup needs the latest prop value.
                const cacheClearOnUnmountRef = useRef(cacheClearOnUnmount);
                useEffect(() => {
                    cacheClearOnUnmountRef.current = cacheClearOnUnmount;
                });
                // No TanStack `QueryClientProvider` on purpose: it would publish this client
                // on TanStack's default context, hijacking the host app's own client below
                // <MessagingRoot>. Manual mount()/unmount() keeps focus/online managers wired.
                useEffect(() => {
                    mountedInstances += 1;
                    queryClient.mount();
                    return () => {
                        mountedInstances -= 1;
                        queryClient.unmount();
                        // The QueryClient is shared by every <MessagingRoot> of this bootstrap:
                        // clearing before the last instance unmounts would wipe the cache of
                        // still-mounted siblings.
                        if (mountedInstances === 0 && cacheClearOnUnmountRef.current) {
                            queryClient.clear();
                        }
                    };
                }, []);
                return (React.createElement(queryClientContext.Provider, { value: queryClient },
                    includeReactQueryDevTools && (React.createElement(ReactQueryDevtools, { client: queryClient, initialIsOpen: false })),
                    React.createElement(MessagingContext.Provider, { value: value }, children)));
            };
            Component.displayName = id ? `${id}Messaging` : "Messaging";
            return { commandBus, queryBus, Component };
        },
    };
    return instance;
};
export { getContext, bootstrap };
